import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { API } from '@/App';
import { useTheme, adminTheme } from '@/context/ThemeContext';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { 
  Search, 
  Plus, 
  Edit2, 
  Trash2, 
  FolderKanban, 
  MoreVertical,
  Building2,
  Cpu,
  CheckCircle2,
  Clock,
  Rocket,
  PauseCircle,
  Eye
} from 'lucide-react';
import { cn } from '@/lib/utils';
import { toast } from 'sonner';

const industryLabels = {
  insurtech: 'Insurtech',
  fintech: 'Fintech',
  oil_gas: 'Oil & Gas',
  telecom: 'Telecom',
  airline: 'Airline',
  others: 'Others',
};

const industryColors = {
  insurtech: 'bg-purple-100 text-purple-700 border-purple-200',
  fintech: 'bg-green-100 text-green-700 border-green-200',
  oil_gas: 'bg-orange-100 text-orange-700 border-orange-200',
  telecom: 'bg-blue-100 text-blue-700 border-blue-200',
  airline: 'bg-cyan-100 text-cyan-700 border-cyan-200',
  others: 'bg-slate-100 text-slate-700 border-slate-200',
};

const statusConfig = {
  planning: { color: 'bg-blue-100 text-blue-700', icon: Clock, label: 'Planning' },
  in_progress: { color: 'bg-yellow-100 text-yellow-700', icon: Rocket, label: 'In Progress' },
  completed: { color: 'bg-green-100 text-green-700', icon: CheckCircle2, label: 'Completed' },
  on_hold: { color: 'bg-gray-100 text-gray-700', icon: PauseCircle, label: 'On Hold' },
};

export default function Projects() {
  const { isDark } = useTheme();
  const navigate = useNavigate();
  const [projects, setProjects] = useState([]);
  const [filteredProjects, setFilteredProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [industryFilter, setIndustryFilter] = useState('all');
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [projectToDelete, setProjectToDelete] = useState(null);

  useEffect(() => {
    fetchProjects();
  }, []);

  useEffect(() => {
    filterProjects();
  }, [projects, searchTerm, industryFilter]);

  const fetchProjects = async () => {
    try {
      const response = await axios.get(`${API}/projects`);
      setProjects(response.data);
    } catch (error) {
      console.error('Error fetching projects:', error);
      toast.error('Failed to fetch projects');
    } finally {
      setLoading(false);
    }
  };

  const filterProjects = () => {
    let filtered = [...projects];

    if (searchTerm) {
      filtered = filtered.filter(
        (proj) =>
          proj.headline?.toLowerCase().includes(searchTerm.toLowerCase()) ||
          proj.technology?.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    if (industryFilter !== 'all') {
      filtered = filtered.filter((proj) => proj.industry === industryFilter);
    }

    setFilteredProjects(filtered);
  };

  const handleDelete = async () => {
    if (!projectToDelete) return;

    try {
      await axios.delete(`${API}/projects/${projectToDelete.id}`);
      setProjects((prev) => prev.filter((proj) => proj.id !== projectToDelete.id));
      toast.success('Project deleted successfully');
    } catch (error) {
      console.error('Error deleting project:', error);
      toast.error('Failed to delete project');
    } finally {
      setDeleteDialogOpen(false);
      setProjectToDelete(null);
    }
  };

  const confirmDelete = (project) => {
    setProjectToDelete(project);
    setDeleteDialogOpen(true);
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 dark:text-white">Projects</h1>
          <p className="text-slate-500 dark:text-slate-400">
            Manage your portfolio projects
          </p>
        </div>
        <Button
          onClick={() => navigate('/admin/projects/add')}
          className="bg-blue-600 hover:bg-blue-700"
        >
          <Plus className="w-4 h-4 mr-2" />
          Add Project
        </Button>
      </div>

      {/* Filters */}
      <Card className="border-0 shadow-md">
        <CardContent className="p-4">
          <div className="flex flex-col sm:flex-row gap-4">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
              <Input
                placeholder="Search projects..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10"
              />
            </div>
            <Select value={industryFilter} onValueChange={setIndustryFilter}>
              <SelectTrigger className="w-full sm:w-48">
                <SelectValue placeholder="Filter by industry" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Industries</SelectItem>
                {Object.entries(industryLabels).map(([value, label]) => (
                  <SelectItem key={value} value={value}>
                    {label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      {/* Projects Grid */}
      {loading ? (
        <div className="flex justify-center py-12">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
        </div>
      ) : filteredProjects.length > 0 ? (
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
          {filteredProjects.map((project) => {
            const status = statusConfig[project.status] || statusConfig.planning;
            const StatusIcon = status.icon;
            const industryColor = industryColors[project.industry] || industryColors.others;

            return (
              <Card
                key={project.id}
                className="border hover:shadow-lg transition-all overflow-hidden group"
              >
                {/* Image */}
                <div className="h-48 bg-gradient-to-br from-blue-600 to-indigo-600 relative overflow-hidden">
                  {project.featured_image ? (
                    <img
                      src={project.featured_image}
                      alt={project.headline}
                      className="w-full h-full object-cover"
                    />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center">
                      <FolderKanban className="w-16 h-16 text-white/30" />
                    </div>
                  )}
                  <Badge className={cn('absolute top-3 left-3', industryColor)}>
                    {industryLabels[project.industry] || 'Other'}
                  </Badge>
                  <Badge className={cn('absolute top-3 right-3', status.color)}>
                    <StatusIcon className="w-3 h-3 mr-1" />
                    {status.label}
                  </Badge>

                  {/* Actions overlay */}
                  <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center gap-2">
                    <Button
                      size="sm"
                      variant="secondary"
                      onClick={() => navigate(`/admin/projects/edit/${project.id}`)}
                    >
                      <Edit2 className="w-4 h-4 mr-1" />
                      Edit
                    </Button>
                    <Button
                      size="sm"
                      variant="destructive"
                      onClick={() => confirmDelete(project)}
                    >
                      <Trash2 className="w-4 h-4 mr-1" />
                      Delete
                    </Button>
                  </div>
                </div>

                <CardContent className="p-5">
                  <h3 className="text-lg font-bold text-slate-900 dark:text-white mb-2 line-clamp-2">
                    {project.headline}
                  </h3>

                  <div className="flex items-center gap-2 text-sm text-slate-500 dark:text-slate-400 mb-3">
                    <Cpu className="w-4 h-4" />
                    <span className="line-clamp-1">{project.technology || 'Not specified'}</span>
                  </div>

                  <div 
                    className="text-sm text-slate-600 dark:text-slate-300 line-clamp-2"
                    dangerouslySetInnerHTML={{ __html: project.details?.substring(0, 150) || '' }}
                  />
                </CardContent>
              </Card>
            );
          })}
        </div>
      ) : (
        <Card className="border-0 shadow-md">
          <CardContent className="py-16 text-center">
            <FolderKanban className="w-16 h-16 text-slate-300 dark:text-slate-600 mx-auto mb-4" />
            <h3 className="text-lg font-semibold text-slate-900 dark:text-white mb-2">
              No projects found
            </h3>
            <p className="text-slate-500 dark:text-slate-400 mb-6">
              {searchTerm || industryFilter !== 'all'
                ? 'Try adjusting your filters'
                : 'Get started by adding your first project'}
            </p>
            {!searchTerm && industryFilter === 'all' && (
              <Button onClick={() => navigate('/admin/projects/add')}>
                <Plus className="w-4 h-4 mr-2" />
                Add Your First Project
              </Button>
            )}
          </CardContent>
        </Card>
      )}

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Project</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete "{projectToDelete?.headline}"? This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDelete}
              className="bg-red-600 hover:bg-red-700"
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
